import type { ManualSection } from '../manual.types';

export const opensipsSection: ManualSection = {
  slug: 'opensips',
  title: 'Rutas y Dialplan (OpenSIPS)',
  icon: 'ki-arrow-up-right',
  summary:
    'Salientes, Entrantes y Dialplan: el flujo previsualizar → aplicar, qué es el reload y quién puede hacer qué.',
  audience: 'QA e ingeniería Core.',
  topics: [
    {
      id: 'que-se-administra',
      title: 'Qué se administra aquí',
      blocks: [
        {
          kind: 'p',
          text: 'Estas tres pantallas escriben directo en la base de **OpenSIPS**, el proxy que enruta las llamadas hacia los carriers. No son configuración de Raptor: son la configuración real del ruteo, y un cambio mal hecho afecta tráfico en producción.'
        },
        {
          kind: 'table',
          head: ['Pantalla', 'Qué controla'],
          rows: [
            [
              '**Salientes**',
              'Los destinos a los que OpenSIPS manda el tráfico, con su capacidad. Es el balanceo de carga.'
            ],
            ['**Entrantes**', 'Qué direcciones IP tienen permitido mandarle tráfico a OpenSIPS.'],
            [
              '**Dialplan**',
              'Reglas de transformación del número marcado: qué buscar y con qué reemplazarlo.'
            ]
          ]
        },
        {
          kind: 'note',
          tone: 'danger',
          title: 'Esto sí toca producción',
          text: 'A diferencia de las pantallas de CPD, aquí no hay una capa de caché que amortigüe: al aplicar, el cambio se escribe y OpenSIPS lo recarga. Por eso el flujo obliga a previsualizar antes.'
        }
      ]
    },
    {
      id: 'previsualizar-aplicar',
      title: 'El flujo: previsualizar antes de aplicar',
      blocks: [
        {
          kind: 'steps',
          items: [
            'Llenas el formulario con los datos del registro.',
            'Presionas **Previsualizar**. El backend calcula el cambio **sin escribir nada** y devuelve exactamente la fila que quedaría, con los nombres de columna reales de OpenSIPS.',
            'En edición se muestra además el **diff contra la fila actual**, para que veas qué cambia y qué no.',
            'Si estás conforme, presionas **Aplicar**. Ahí sí se escribe **y** se dispara la recarga de OpenSIPS.',
            'Aparece un aviso con el resultado de esa recarga. Léelo: escribir y recargar son dos cosas distintas y la segunda puede fallar sola.'
          ]
        },
        {
          kind: 'note',
          tone: 'warn',
          title: 'Si el aviso de recarga dice que falló',
          text: 'El registro **ya quedó escrito** en la base, pero OpenSIPS sigue trabajando con la configuración vieja en memoria. Usa el botón **Recargar** de la pantalla para volver a intentarlo. No vuelvas a aplicar el formulario: duplicarías el registro.'
        }
      ]
    },
    {
      id: 'campos',
      title: 'Campos de cada pantalla',
      blocks: [
        { kind: 'h4', text: 'Salientes' },
        {
          kind: 'kv',
          items: [
            { k: 'Grupo', v: 'Identificador numérico del grupo de balanceo.' },
            { k: 'Destino (URI)', v: 'A dónde se manda el tráfico. Hasta 128 caracteres.' },
            {
              k: 'Recursos',
              v: 'Al menos uno. Cada uno con su nombre (hasta 60 caracteres) y su **capacidad** en llamadas simultáneas.'
            },
            {
              k: 'Modo de sondeo',
              v: '0, 1 o 2 — cómo comprueba OpenSIPS que el destino está vivo.'
            },
            { k: 'Canales máximos', v: 'Tope de canales para ese destino.' },
            { k: 'Descripción', v: 'Texto libre, hasta 128 caracteres.' }
          ]
        },
        {
          kind: 'p',
          text: 'El botón **Capacidad** de la tabla permite cambiar solo la capacidad de un destino, sin abrir el formulario completo. Es la operación más frecuente del día a día.'
        },
        { kind: 'h4', text: 'Entrantes' },
        {
          kind: 'kv',
          items: [
            { k: 'Grupo', v: 'Número de 0 a 65 535.' },
            { k: 'IP y máscara', v: 'La dirección autorizada y su máscara (0 a 128).' },
            { k: 'Puerto', v: 'Puerto de origen.' },
            { k: 'Protocolo', v: '`any`, `udp`, `tcp`, `tls` o `sctp`.' },
            { k: 'Patrón', v: 'Hasta 64 caracteres.' },
            { k: 'Contexto', v: 'Hasta 32 caracteres.' },
            { k: 'Descripción', v: 'Hasta 300 caracteres.' }
          ]
        },
        { kind: 'h4', text: 'Dialplan' },
        {
          kind: 'kv',
          items: [
            { k: 'dpid', v: 'Identificador del conjunto de reglas.' },
            { k: 'Prioridad (pr)', v: 'Orden de evaluación dentro del conjunto.' },
            {
              k: 'Operador de match',
              v: '`equal` (coincidencia exacta) o `regex` (expresión regular).'
            },
            { k: 'Expresión de match', v: 'Qué se busca. Hasta 500 caracteres.' },
            {
              k: 'Expresión de sustitución / reemplazo',
              v: 'Con qué se reemplaza. Hasta 64 y 32 caracteres.'
            },
            { k: 'timerec', v: 'Restricción de horario, hasta 255 caracteres.' },
            {
              k: 'Habilitada',
              v: 'Se puede prender y apagar desde la tabla, sin abrir el formulario.'
            }
          ]
        },
        {
          kind: 'note',
          tone: 'warn',
          title: 'Cuidado con la prioridad en Dialplan',
          text: 'Las reglas se evalúan en orden de prioridad. Insertar una regla con prioridad baja puede hacer que otra ya nunca se alcance. Previsualiza y revisa el conjunto completo antes de aplicar.'
        }
      ]
    },
    {
      id: 'permisos-opensips',
      title: 'Quién puede hacer qué',
      blocks: [
        {
          kind: 'table',
          head: ['Acción', 'operador', 'supervisor', 'admin', 'superadmin'],
          align: ['start', 'center', 'center', 'center', 'center'],
          rows: [
            ['Ver las tres pantallas', '✓', '✓', '✓', '✓'],
            ['Crear / editar / habilitar', '', '✓', '✓', '✓'],
            ['Recargar Entrantes y Dialplan', '', '✓', '✓', '✓'],
            ['**Recargar Salientes**', '', '', '✓', '✓']
          ],
          caption:
            'Recargar Salientes toca el balanceo de carga de toda la plataforma, por eso pide admin.'
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Si no ves un botón, es por tu rol',
          text: 'La pantalla esconde lo que tu rol no puede hacer. El backend valida igual, así que aunque el botón apareciera la operación sería rechazada.'
        },
        {
          kind: 'note',
          tone: 'warn',
          title: 'Los cambios aquí todavía no quedan en Auditoría',
          text: 'La pantalla de Auditoría registra autenticación, usuarios y CPD. Salientes, Entrantes y Dialplan **aún no** se registran: es un pendiente conocido del backend. Mientras tanto, los cambios importantes en estas tres pantallas conviene anotarlos por fuera.'
        }
      ]
    }
  ]
};
